import React, { useState, useEffect, useContext } from 'react';
import { CircularProgress, Box, Typography, TableSortLabel, TableCell } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MUIDataTable from "mui-datatables";
import axios from 'axios';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import AuthContext from '../../contexts/AuthContext';
import { useDispatch } from 'react-redux';
import { setMetaData, setSweetAlert, setSnackDetails } from '../../service/actions/common';

const ContractorsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [contractors, setContractors] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState( parseInt(process.env.REACT_APP_PER_PAGE) );
  const [page, setPage] = useState(0);
  const [sortOrder, setSortOrder] = useState({ name: 'company_name', direction: 'desc' });
  const { token, user } = useContext(AuthContext); 

  const metaData  = {
    title: 'Contractor Users | Inspect Reply AI',
    description: 'Contractor Users | Inspect Reply AI'
  };

  const catchError = async (error) => {
    if (error.response) {
      if (error.response.status === 401) {
          navigate('/sign-in');
      } else if (error.response.status >= 500) {
          dispatch(setSweetAlert({ open: true, title: 'Server Error', text: '' }));
      } else if (error.response.status === 400) {
          dispatch(setSnackDetails({openState : true, message : error.response.data.message, severity : 'error'}));
          return false;
      } else {
          dispatch(setSweetAlert({ open: true, title: 'Error', text: '' }));
      }
  } else if (error.request) {
      dispatch(setSweetAlert({ open: true, title: 'Connection Lost', text: '' }));
  } else {
      dispatch(setSweetAlert({ open: true, title: 'Error', text: 'An unknown error occurred.' }));
  }
  };

  	const getMuiTheme = () =>
    createTheme({
      components: {
        MuiTableSortLabel: {
          styleOverrides: {
            icon: {
            opacity: '1 !important', // Ensure sorting icons are always visible
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            head: {
              '& .MuiButtonBase-root': {
                fontWeight: 'bold', // Make sorting label text bold as well
                color: 'inherit', // Keep color inheritance for both text and icon
              },
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.MuiTableRow-root': {
                  cursor: 'pointer',
              },
              "&:nth-of-type(odd)": {
                backgroundColor: "#FFFFFF",
              },
              "&:nth-of-type(even)": {
                backgroundColor: "#D8D8D8",
              },
              "& .MuiTableCell-head": {
                  backgroundColor: "#D8D8D8",
                  fontWeight: "bold"
              },
            },
          },
        },
      },
    });

	// Handle sort change
	const handleSortChange = (columnName) => {
		const isAsc = sortOrder.name === columnName && sortOrder.direction === 'asc';
		setSortOrder({ name: columnName, direction: isAsc ? 'desc' : 'asc' });
		setPage(0);
	};

	const handlePageChange = (currentPage) => {
		setPage(currentPage);
	};

  const fetchUsers = async (page, perPage, searchText, sortOrder) => {
	if (searchText.trim() === '') {
    	setLoading(true);
	}
    if (user && token) {
      try {
          let uri = `/contractors?skip=${page * perPage}&limit=${perPage}&sort=${sortOrder.name}&sort_dir=${sortOrder.direction}`
          if (searchText.trim() !== '') {
            uri += `&search=${encodeURI(searchText.trim())}`
          }
          const response = await axios.get(uri, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          setContractors(response.data.contractors);
          setTotalRows(response.data.total);
          setLoading(false);
      } catch (error) {
          catchError(error);
      }
    }
  };

  useEffect(() => {
    fetchUsers(page, perPage, searchText, sortOrder);
    dispatch(setMetaData(metaData));
    // eslint-disable-next-line
  }, [page, perPage, searchText, sortOrder]);

  	// Common customHeadRender function
	const customHeadRender = (columnMeta) => (
		<TableCell key={columnMeta.name}>
		<Box display="flex" alignItems="center">
			<Typography variant="body1" sx={{ fontWeight: 'bold' }}>{columnMeta.label}</Typography>
			<TableSortLabel
        active={sortOrder.name === columnMeta.name}
        direction={sortOrder.name === columnMeta.name ? sortOrder.direction : 'desc'}
        onClick={() => handleSortChange(columnMeta.name)}
			/>
		</Box>
		</TableCell>
	);

	const handleSearchChange = (value) => {
		if (value.length > 2 ) {
			setSearchText(value.trim());
			setPage(0); // Reset to first page on search
		} else if (value.length === 0) {
			setSearchText(" ");
		}
	};

  const handleRow = async (rowData, rowMeta) => {
    const contractor_id = rowData[0];
    navigate('/contractor-users/' + contractor_id);
  };

  const columns = [
    {
      name: "_id",
      options: {
        display: false
      }
     },
    {
     name: "company_name",
     label: "Name",
     options: {
      filter: true,
      sort: true,
	  customHeadRender: (columnMeta) => customHeadRender(columnMeta),
     }
    },
    {
     name: "email",
     label: "Email",
     options: {
      filter: true,
      sort: true,
	  customHeadRender: (columnMeta) => customHeadRender(columnMeta),
     }
    },
    {
      name: "phone",
      label: "Phone",
      options: {
       filter: true,
       sort: true,
	   customHeadRender: (columnMeta) => customHeadRender(columnMeta),
       customBodyRender: (value, tableMeta, updateValue) => {
          var cleaned = ('' + value).replace(/\D/g, '');
          var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
          if (match) {
            return match[1] + '-' + match[2] + '-' + match[3];
          }
          return null;
      }
      }
     },
     {
      name: "city_name",
      label: "City",
      options: {
       filter: true,
       sort: true,
       customHeadRender: (columnMeta) => customHeadRender(columnMeta),
      }
     },
     {
      name: "state_name",
      label: "State",
      options: {
       filter: true,
       sort: true,
       customHeadRender: (columnMeta) => customHeadRender(columnMeta),
      }
     },
     {
      name: "zip_code",
      label: "ZIP",
      options: {
       filter: true,
       sort: true,
      }
     },
     {
      name: "customer_email",
      label: "Belongs To",
      options: {
       filter: true,
       sort: true
      }
     }
  ];

  const options = {
	  sort: true,
    serverSide: true,
    viewColumns: false,
    count: totalRows,
    rowsPerPage: perPage,
    rowsPerPageOptions: process.env.REACT_APP_PER_PAGE_OPTION.split(",").map(Number),
    page: page,
    searchText: searchText,
    loading: loading,
    selectableRows: 'none',
    print: false,
    filter: false,
    download: true,
	sortOrder: {
		name: sortOrder.name,
		direction: sortOrder.direction,
	},
    onRowClick:handleRow,
    onTableChange: (action, tableState) => {
      if (action === "changePage") {
        handlePageChange(tableState.page);
      } else if (action === "search") {
        handleSearchChange(tableState.searchText || "");
      }  else if (action === "onSearchClose") {
        handleSearchChange(""); // reset searchText to null
      } else if (action === 'changeRowsPerPage') {
        setPerPage(tableState.rowsPerPage);
        setPage(0);
      } else if (action === 'onSearchClose') {
        setSearchText("");
        setPage(0);
      }
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const alteredData = data?.map((row, index) => ({
        index,
        data: row?.data?.map((field, index) => {
                  if (columns[index].customBodyRender) {
                      return columns[index].customBodyRender(field)
                  }
                  return field
              }),
      }))
      return `${buildHead(columns)}${buildBody(alteredData)}`.trim()
    },
    textLabels: {
      toolbar: {
        search: "Search",
        downloadCsv: "Download CSV",
      },
      body: {
        noMatch: 'No results matching your filter. Try removing the Filter',
      }
    },
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Typography variant="h4" gutterBottom>Preferred Contractors</Typography>
      
      <Box sx={{ backgroundColor:'#FFF', p:2, height: '100vh' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              data={contractors}
              columns={columns}
              options={options}
            />
          </ThemeProvider>
        )}
      </Box>
    </Box>
  );
};

export default ContractorsList;
